<template>
    <div>
        <ReportTemplate 
            ref="reportcomponent" 
            :table-props="downloadsColumns" 
            @editableGridUpdated="onEditableGridUpdate"/>
        
        <FloatingComponent 
            :changes="changes" 
            :floating-view-configs="floatingViewConfigs" 
            @discardEditableGridChanges="discardChanges" 
            @submitEditableGridChanges="submitChanges" />
    </div>
</template>

<script>
import ReportTemplate from "@/components/ReportTemplate.vue";
import Store from "../vuex/store.js";
import {
    type,
    pageViews,
    app_mode,
    user,
    formatDateConsistent
} from "../utils/util";
import FloatingComponent from "@/components/FloatingComponent.vue";
import { getCookie } from "../store/cookie.js";
import { useAuthStore } from "../store/auth.js";

export default {
    name: pageViews.DOWNLOADS_VIEW,
    components: {
        ReportTemplate,
        FloatingComponent,
    },
    mounted() {
        // Apply row restrictions for downloads-specific handling
        this.$nextTick(() => {
            if (this.$refs.reportcomponent) {
                // Override the ReportTemplate's collateRequestIDs method for single selection
                this.$refs.reportcomponent.collateRequestIDs = (requestId) => {
                    // Clear all previous selections
                    this.$refs.reportcomponent.selectedReqsForBulkOps = [];
                    this.$refs.reportcomponent.allSelectedReqs = new Set();
                    
                    // Add only the clicked item (single selection)
                    this.$refs.reportcomponent.selectedReqsForBulkOps = [requestId];
                    this.$refs.reportcomponent.allSelectedReqs = new Set([requestId]);
                };
                
                // Apply row restrictions with multiple triggers
                setTimeout(() => {
                    this.disableNonSelectableRows();
                }, 100);
                
                setTimeout(() => {
                    this.disableNonSelectableRows();
                }, 500);
                
                setTimeout(() => {
                    this.disableNonSelectableRows();
                }, 1000);
                
                setTimeout(() => {
                    this.disableNonSelectableRows();
                }, 2000);
                
                // Also listen for table updates
                const observer = new MutationObserver(() => {
                    this.disableNonSelectableRows();
                });
                
                if (document.querySelector('table')) {
                    observer.observe(document.querySelector('table'), {
                        childList: true,
                        subtree: true
                    });
                }
                
                // Add global click interceptor for non-selectable rows
                document.addEventListener('click', (event) => {
                    const clickedElement = event.target;
                    const row = clickedElement.closest('tr');
                    
                    if (row && row.classList.contains('non-downloadable-row')) {
                        event.preventDefault();
                        event.stopPropagation();
                        return false;
                    }
                });
            }
        });
    },
    methods: {
        // Check if a row can be selected for download (only completed/successful downloads)
        isRowSelectable(transaction) {
            if (!transaction || !transaction.status) {
                return false;
            }
            
            const status = transaction.status.toLowerCase().trim();
            const isSelectable = status === 'ok';
            
            return isSelectable;
        },

        // Override the ReportTemplate's collateRequestIDs method for single selection
        collateRequestIDs(requestId) {
            // Get the ReportTemplate component reference
            const reportComponent = this.$refs.reportcomponent;
            
            if (reportComponent) {
                // Clear all selections first
                reportComponent.selectedReqsForBulkOps = [];
                reportComponent.allSelectedReqs = new Set();
                
                // Only add the clicked item (single selection)
                reportComponent.selectedReqsForBulkOps = [requestId];
                reportComponent.allSelectedReqs = new Set([requestId]);
            }
        },

        // Method to disable non-selectable rows and handle single row selection
        disableNonSelectableRows() {
            const reportData = this.$refs.reportcomponent?.reportTransactions?.transactions || [];
            
            this.$nextTick(() => {
                // Find all table rows (exclude header)
                const tableRows = document.querySelectorAll('tbody tr');
                
                tableRows.forEach((row, index) => {
                    const transaction = reportData[index];
                    
                    if (transaction) {
                        const isSelectable = this.isRowSelectable(transaction);
                        
                        if (!isSelectable) {
                            // Disable non-selectable rows
                            row.classList.remove('downloadable-row');
                            row.classList.add('non-downloadable-row');
                            row.style.pointerEvents = 'none';
                            row.style.opacity = '0.6';
                            row.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                            row.style.cursor = 'not-allowed';
                        } else {
                            // Enable selectable rows for single selection
                            row.classList.remove('non-downloadable-row');
                            row.classList.add('downloadable-row');
                            row.style.pointerEvents = 'auto';
                            row.style.opacity = '1';
                            row.style.backgroundColor = 'rgba(40, 167, 69, 0.1)';
                            row.style.cursor = 'pointer';
                            
                            // Add click handler for single row selection
                            row.addEventListener('click', (event) => {
                                event.preventDefault();
                                
                                // Remove selection from all other rows
                                tableRows.forEach(otherRow => {
                                    if (otherRow !== row) {
                                        otherRow.classList.remove('selected-row');
                                        otherRow.style.backgroundColor = 'rgba(40, 167, 69, 0.1)';
                                    }
                                });
                                
                                // Toggle selection on clicked row
                                if (row.classList.contains('selected-row')) {
                                    row.classList.remove('selected-row');
                                    row.style.backgroundColor = 'rgba(40, 167, 69, 0.1)';
                                } else {
                                    row.classList.add('selected-row');
                                    row.style.backgroundColor = 'rgba(40, 167, 69, 0.3)';
                                }
                            });
                        }
                    }
                });
                
            });
        },

        setValuesToStore(type) {
            Store.commit("setType", type);
        },
        onEditableGridUpdate(changes) {
            this.changes = changes;
        },
        discardChanges() {
            this.$refs.reportcomponent.discardEditableGridChanges();
            this.changes = {};
        },
        submitChanges() {
            try{
                this.$refs.reportcomponent.submitEditableGridChanges();
                this.changes = {};
            }catch(error){
                // Error handling for submitting changes
            }
        },
    },
    data() {
        return {
            status: true,
            floatingViewConfigs: {
                title: "Download Updates",
                type: type.DOWNLOADS,
            },
            downloadsColumns: {
                type: type.DOWNLOADS,
                defaultSortColumn: "creaTs",
                defaultSortOrder: "desc",
                configs: [
                    {
                        name: "downloadId",
                        label: "Download ID", 
                        order: 1,
                        defaultSelected: true,
                        textAlign: "left",
                        key: "downloadId"
                    },
                    {
                        name: "projectId",
                        label: "Project ID",
                        order: 2,
                        defaultSelected: true,
                        textAlign: "left"
                    },
                    {
                        name: "type",
                        label: "Type",
                        order: 3,
                        defaultSelected: true,
                        textAlign: "left"
                    },
                    {
                        name: "fileName",
                        label: "File Name",
                        order: 4,
                        defaultSelected: true,
                        checked: true,
                        textAlign: "left"

                    },
                    {
                        name: "status",
                        label: "Status",
                        order: 5,
                        defaultSelected: true,
                        textAlign: "center"
                    },
                    {
                        name: "creaTs",
                        label: "Requested On",
                        order: 6,
                        defaultSelected: true,
                        type: "date",
                    },
                    {
                        name: "creaTs",
                        label: "Expires On",
                        order: 7,
                        defaultSelected: true,
                        type: "date",
                        formatter: (creaTs) => {
                            if (!creaTs) return '-';
                            const requestedDate = new Date(creaTs);
                            const expiresDate = new Date(requestedDate);
                            expiresDate.setMonth(expiresDate.getMonth() + 1);
                            
                            // Use consistent formatting across all platforms (Windows/Mac/Linux)
                            return formatDateConsistent(expiresDate);
                        }
                    },
                    {
                        name: "remarks",
                        label: "Remarks",
                        order: 8,
                        defaultSelected: true,
                        textAlign: "left"
                    },
                ],
                actions: {
                    download: {
                        label: "Download Content"
                    }
                },
                isMultiSelectionAllowed: false,
                // Add row selection validator for downloads - only allow 'ok' status
                rowSelectionValidator: this.isRowSelectable,
                // Add custom row classes for visual indication
                rowClassFunction: (transaction) => {
                    if (!transaction || !transaction.status) {
                        return 'non-downloadable-row';
                    }
                    const status = transaction.status.toLowerCase().trim();
                    return status === 'ok' ? 'downloadable-row' : 'non-downloadable-row';
                },
            },
            changes: {},
        }
    },
    watch: {
        // Watch for data changes and re-apply row selection restrictions
        '$refs.reportcomponent.reportTransactions': {
            handler() {
                this.$nextTick(() => {
                    this.disableNonSelectableRows();
                });
            },
            deep: true
        }
    }
}
</script>

<style scoped>
/* Add any custom styles for the downloads view here */
.downloads-view {
    padding: 20px;
}

.downloads-header {
    margin-bottom: 20px;
}

.downloads-title {
    color: #333;
    font-size: 24px;
    font-weight: 600;
}

/* Visual indication for downloadable vs non-downloadable rows */
.downloadable-row {
    background-color: rgba(40, 167, 69, 0.1) !important; /* Light green tint */
    cursor: pointer !important;
}

.downloadable-row:hover {
    background-color: rgba(40, 167, 69, 0.15) !important; /* Slightly darker green on hover */
}

.selected-row {
    background-color: rgba(40, 167, 69, 0.3) !important; /* Darker green for selected row */
}

.non-downloadable-row {
    background-color: rgba(220, 53, 69, 0.1) !important; /* Light red tint */
    opacity: 0.6 !important;
    cursor: not-allowed !important;
    pointer-events: none !important;
}

.non-downloadable-row td {
    color: #6c757d !important; /* Gray text */
}
</style>
